import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-xl border border-transparent bg-clip-padding font-medium max-md:text-base md:text-lg 2xl:text-xl focus-visible:ring-3 aria-invalid:ring-3 [&_svg:not([class*='size-'])]:size-4 inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none",
  {
    variants: {
      colorMode: {
        light: "",
        dark: "",
      },
      variant: {
        default:
          "bg-foreground text-background hover:bg-foreground/80 [a]:hover:bg-foreground/80",
        outline:
          "bg-transparent hover:bg-muted hover:text-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 aria-expanded:bg-muted aria-expanded:text-foreground",
        secondary:
          "bg-black text-white hover:bg-neutral-800 hover:text-white aria-expanded:bg-black aria-expanded:text-white",
        ghost:
          "hover:bg-muted hover:text-foreground dark:hover:bg-[#f1f0ee] aria-expanded:bg-muted aria-expanded:text-foreground",
        destructive:
          "bg-destructive/10 hover:bg-destructive/20 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/20 text-destructive focus-visible:border-destructive/40 dark:hover:bg-destructive/30",
        link: "text-primary underline-offset-4 hover:underline px-2.5 py-1.5 max-md:text-sm md:text-md",
        "ghost-no-active":
          "hover:text-foreground dark:hover:bg-[#f1f0ee] aria-expanded:text-foreground",
      },
      size: {
        default:
          "h-8 md:h-9 2xl:h-10 gap-1.5 p-4 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        xs: "h-6 md:h-7 2xl:h-8 gap-1 rounded-[min(var(--radius-md),10px)] p-3 in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 md:h-8 2xl:h-9 gap-1 rounded-[min(var(--radius-md),12px)] p-4 in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-10 md:h-11 2xl:h-12 gap-1.5 p-5 has-data-[icon=inline-end]:pr-4 has-data-[icon=inline-start]:pl-4",
        xl: "max-md:text-base md:text-lg 4xl:text-xl h-12 md:h-13 2xl:h-14 gap-1.5 max-md:p-5 md:p-6 4xl:p-7 has-data-[icon=inline-end]:pr-5 has-data-[icon=inline-start]:pl-5",
        "2xl":
          "max-md:text-lg md:text-xl 4xl:text-2xl h-13 md:h-14 2xl:h-14 gap-2 max-md:p-6 md:p-7 4xl:p-8 has-data-[icon=inline-end]:pr-6 has-data-[icon=inline-start]:pl-6",
        "link-xl": "max-md:text-sm md:text-base 2xl:text-lg",
        icon: "size-8 md:size-9 2xl:size-10",
        "icon-xs":
          "size-6 md:size-7 2xl:size-8 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "size-7 md:size-8 2xl:size-9 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg",
        "icon-lg": "size-9 md:size-10 2xl:size-11",
        "link-lg":
          "text-lg gap-1 text-foreground underline-offset-4 hover:underline px-2.5 py-1.5 text-base md:text-md",
      },
    },
    compoundVariants: [
      {
        colorMode: "light",
        variant: "outline",
        className: "border-[0.15em] border-neutral-900/80 text-foreground",
      },
      {
        colorMode: "dark",
        variant: "default",
        className:
          "bg-white text-black hover:bg-white/85 [a]:hover:bg-white/85",
      },
      {
        colorMode: "dark",
        variant: "outline",
        className:
          "border-[0.15em] border-white bg-transparent text-white hover:bg-white/10 hover:text-white aria-expanded:bg-white/10 aria-expanded:text-white",
      },
      {
        colorMode: "dark",
        variant: "secondary",
        className:
          "bg-[#363530] text-white hover:bg-[#403d39] hover:text-white aria-expanded:bg-[#363530] aria-expanded:text-white",
      },
      {
        colorMode: "dark",
        variant: "ghost",
        className:
          "text-white hover:bg-white/10 hover:text-white aria-expanded:bg-white/10 aria-expanded:text-white",
      },
      {
        colorMode: "dark",
        variant: "ghost-no-active",
        className:
          "text-white hover:bg-white/10 hover:text-white aria-expanded:text-white",
      },
      {
        colorMode: "dark",
        variant: "link",
        className: "text-white hover:text-white",
      },
      {
        colorMode: "dark",
        variant: "destructive",
        className:
          "border-destructive/20 bg-destructive/15 text-red-200 hover:bg-destructive/25",
      },
      {
        colorMode: "dark",
        size: "link-lg",
        className: "text-white",
      },
    ],
    defaultVariants: {
      colorMode: "light",
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

function Button({
  className,
  colorMode = "light",
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : "button";

  return (
    <Comp
      data-slot="button"
      data-color-mode={colorMode}
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ colorMode, variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
